import {Component, OnInit} from '@angular/core';
import {NgForm} from '@angular/forms/forms';
import {DirectoryService} from '../../../../services/directory.service';
import {ApiClientService} from '../../../../services/api-client.service';
import {Resident, UserPhoneType, UserAddressType} from '../../../../types/data.types';
import {AbstractCreateModalComponent} from '../abstract-create-modal';
import {ConstantsService} from '../../../../services/constants.service';

declare const $: any;
declare const toastr: any;
declare const swal: any;

@Component({
    selector: 'resident-create',
    templateUrl: './resident-create.component.html',
})

export class ResidentCreateComponent extends AbstractCreateModalComponent implements OnInit {
    data = new Resident();
    title = 'Resident';
    multiselectSettings = {
        singleSelection: false,
        text: 'Select Groups',
        selectAllText: 'Select All',
        unSelectAllText: 'Deselect All',
        enableSearchFilter: true
    };
    phoneTypes = [
        {type: 'phone_home', label: 'Home'},
        {type: 'phone_cell', label: 'Cell'},
        {type: 'phone_business', label: 'Business'},
        {type: 'phone_fax', label: 'Fax'},
        {type: 'phone_business_fax', label: 'Business Fax'},
        {type: 'phone_other', label: 'Other'},
    ];

    dateOfBirth: Date;
    occupancyDate: Date;
    leaseStartDate: Date;
    primaryPhone   = 'phone[0]';
    primaryAddress = 'address[0]';
    selectedGroup = [];
    emergencyContacts = [];
    showEmailError = false;
    errorText = '';
    disabledRestore = false;
    archivedUser = null;
    autoInviteResidents = null;

    constructor(
        public directoryService: DirectoryService,
        public api: ApiClientService
    ) {
        super(directoryService, api);

        this.data.phones.push(new UserPhoneType());
        this.data.addresses.push(new UserAddressType());
    }

    ngOnInit() {
        super.ngOnInit();
        this.directoryService.getGroups();
    }

    onSubmit(form: NgForm): void {
        if (!form.valid) {
            toastr.warning('Send data not valid');
            if (!form.value.building) toastr.warning('Building should not be blank.');
            if (!form.value.suite)    toastr.warning('Unit should not be blank.');
            if (!form.value.fullName) toastr.warning('Resident Full Name should not be blank.');
            return;
        }
        if (this.autoInviteResidents == null) { 
            toastr.warning('Please wait! Corporation settings not loaded yet.');
            return;
        }
        const building = this.directoryService.buildingsForUpdate.find(building => building.id == form.value.building);
        const suite = this.suites.find(suite => suite.id == form.value.suite);
        const groups = form.value.groups.map( g => ({ group: +g.id}) );
        if(form.value.emergencyContacts && !this.dataIsValid(form.value.emergencyContacts))
            return;
        
        let autoInvite = 0;
        if (this.autoInviteResidents != null) { 
            autoInvite = form.value.inviteInput ? 1 : 0;
        }
        const data = {
            suite: form.value.suite,
            suiteNumber: suite.suiteNumber,
            building: form.value.building,
            buildingName: building.name,
            buildingAddress: building.address,
            corpNumber: building.corporationNumber,
            user: {
                email: form.value.email,
                fullName: form.value.fullName,
                dateOfBirth: this.dateOfBirth,
                addresses: this.getAddressesFromForm(form),
                phones: this.getPhonesFromForm(form),
                groups: groups,
                emergencyContacts: form.value.emergencyContacts.map(c => ({...c, suite: form.value.suite})),
                emergencyAssistantNotes: form.value.emergencyAssistantNotes,
                maxTvUser: 0,
                emailWaiverSigned: form.value.emailWaiverSigned,
                parcelWaiverSigned: form.value.parcelWaiverSigned,
                keyWaiverSigned: form.value.keyWaiverSigned,
                ownerInformationFormReceived: form.value.role == 'owner_on' || form.value.role == 'owner_off' ? form.value.ownerInformationFormReceived : undefined,
                leaseReceived: form.value.role == 'rent' ? form.value.leaseReceived : undefined,
                tenantInformationFormReceived: form.value.role == 'rent' ? form.value.tenantInformationFormReceived : undefined,
                primaryLeaseHolder: form.value.role == 'rent' ? form.value.primaryLeaseHolder : undefined,
                leaseStartDate: form.value.role == 'rent' ? form.value.leaseStartDate : undefined
            },
            occupancyDate: this.occupancyDate,
            role: form.value.role,
            autoInvite: autoInvite
        };

        this.directoryService.currentTabId = 'residents';
        this.create(data);
    }
    
    getSuitesByBuildingId(value) { 
        super.getSuitesByBuildingId(value);
        
        let corpId = this.directoryService.corporations.find(corp =>
            corp.buildings.find(b => b == String(value))
        ).id;
        
        const url = `/v1/corporation_number/${corpId}.json?expand=settings`;
        this.api.get('domainBD', url).subscribe(
            (res) => {
                const body = JSON.parse(res._body);
                this.autoInviteResidents = body.settings.autoInviteResidents;
            },
            error => this.api.errorHandler(error, 'Failed get Services')
        );
    }

    phoneInputFocusout(value: string, i: number): void {
        this.data.phones[i].phone = value;
        const phoneField = this.form.controls[`phone[${i}]`];
        if (phoneField) {
            phoneField.markAsDirty();
        }
    }

    getPhonesFromForm(form: NgForm): Array<UserPhoneType> {
        const phones: Array<UserPhoneType> = [];
        this.data.phones.forEach((phoneBlock, index) => {
            if (!form.value[`phone[${index}]`]) return;
            const phone = new UserPhoneType(
                (form.value[`isPrimaryPhone`] === `phone[${index}]`),
                form.value[`phone[${index}]`],
                form.value[`phone_type[${index}]`]
            );
            phones.push(phone);
        });
        return phones;
    }

    getAddressesFromForm(form: NgForm): Array<UserAddressType> {
        const addresses: Array<UserAddressType> = [];
        this.data.addresses.forEach((addressBlocks, index) => {
            if (!form.value[`address[${index}]`]) return;
            const address = new UserAddressType(
                (form.value[`isPrimaryAddress`] === `address[${index}]`),
                form.value[`country[${index}]`],
                form.value[`state[${index}]`],
                form.value[`city[${index}]`],
                form.value[`address[${index}]`],
                form.value[`postalCode[${index}]`]
            );
            addresses.push(address);
        });
        return addresses;
    }

    addNewPhone(): void {
        const isFirst = this.data.phones.length === 0;
        this.data.phones.push(new UserPhoneType(isFirst));
    }

    deletePhone(index): void {
        const phoneField = this.form.controls[`phone[${index}]`];

        if (phoneField && phoneField.value) {
            swal(ConstantsService.getSwalConfig('Delete Phone'),
                () => {
                    this.data.phones.splice(index, 1);
                    if (!this.data.phones.some(p => p.isPrimaryPhone)) {
                        this.primaryPhone = 'phone[0]';
                    }
                }
            );
        } else {
            this.data.phones.splice(index, 1);
        }

        if (this.data.phones.length === 0) {
            this.data.phones.push(new UserPhoneType());
        }
    }

    addNewAddress(): void {
        const isFirst = this.data.addresses.length === 0;
        this.data.addresses.push(new UserAddressType(isFirst));
    }

    deleteAddress(index): void {
        const addressField = this.form.controls[`address[${index}]`];

        if (addressField && addressField.value) {
            swal(ConstantsService.getSwalConfig('Delete Address'),
                () => {
                    this.data.addresses.splice(index, 1);
                    if (!this.data.addresses.some(p => p.isPrimaryAddress)) {
                        this.primaryAddress = 'address[0]';
                    }
                }
            );
        } else {
            this.data.addresses.splice(index, 1);
        }

        if (this.data.addresses.length === 0) {
            this.data.addresses.push(new UserAddressType());
        }
    }

    private dataIsValid(value) {
        let valid = true;
        let hasEmpty = false;
        value.forEach((item) => {
            if(!item.name && !item.phone && !item.email && !item.relation && !item.note) {
                valid = false;
                hasEmpty = true;
            } else if(item.email && !this.emailIsValid(item.email)) {
                toastr.warning(`Email ${item.email} is invalid.`);
                valid = false;
            }
        });
        if(hasEmpty)
            toastr.warning('At least one field per item must be field.');
        return valid;
    }

    emailIsValid(email) {
        return email.match('[a-zA-Z0-9!#$%&\'*+/=?^_`{|}~-]+(?:\\.[a-zA-Z0-9!#$%&\'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?');
    }

    onBlurEmailInput(event) {
        if(this.form.value.suite && this.emailIsValid(this.form.value.email))
            this.tryGetArchivedResidentDetails(this.form.value.suite, this.form.value.email);
    }

    onSuiteChange(event) {
        if(this.form.value.email && this.emailIsValid(this.form.value.email))
            this.tryGetArchivedResidentDetails(event, this.form.value.email);
    }

    tryGetArchivedResidentDetails(suite, email) {
        this.disabledSubmit = true;
        this.showEmailError = false;
        this.archivedUser = null;
        const url = `/v1/suite-users.json?expand=user&suite_id=${suite}&user_email=${encodeURIComponent(email)}&show_moved_out=true`;
        this.api.get('domainBD', url).subscribe(
            res => {
                const body = JSON.parse(res._body);
                if(body.entities.length > 0) {
                    this.showEmailError = true;
                    const resident = body.entities[0];
                    if(resident.moveOutDate && new Date(resident.moveOutDate) < new Date()) {
                        this.archivedUser = resident;
                        this.errorText = `This email address can’t be used for resident. It belongs to another archived resident (${resident.user.fullName}, ${resident.user.email}${this.getPrimaryPhone(resident.user.phones)})`;
                    } else
                        this.errorText = `This email address can’t be used for resident. It belongs to another resident (${resident.user.fullName}, ${resident.user.email}${this.getPrimaryPhone(resident.user.phones)})`;
                } else
                    this.disabledSubmit = false;
            },
            error => this.disabledSubmit = false
        );
    }

    restoreResident() {
        this.disabledRestore = true;
        const url = `/v1/suite-users/${this.archivedUser.id}`;
        const data = {moveOutDate: null};

        this.api.patch('domainBD', url, data).subscribe(
            res => {
                this.disabledSubmit = false;
                this.disabledRestore = false;

                toastr.success('Resident was restored successfully.');
                if (this.inDirectoryItemView)
                    this.onPopupClose.emit();
                else
                    this.directoryService.getData();
                this.directoryService.hideModal();
            }, error => {
                this.disabledSubmit = false;
                this.disabledRestore = false;
                this.api.errorHandler(error, 'Failed to restore resident');
            }
        );
    }

    getPrimaryPhone(phones) {
        if(phones.length > 0) {
            const primary = phones.find(p => p.isPrimaryPhone);
            return ', ' + (primary ? primary.phone : phones[0].phone);
        } else
            return '';
    }

    onEmailChange(event) {
        this.disabledSubmit = false;
        this.showEmailError = false;
        this.archivedUser = null;
    }
}



// WEBPACK FOOTER //
// /app/src/app/components/directory/create-modal/resident-create/resident-create.component.ts